import * as _ from 'lodash';
import { useCallback } from 'react';

import { useAsyncRequest } from '../../../../../hooks';
import { post } from '../../../../../redux/api';
import type { ProjectsFilter, ProjectsResponse } from '../../../../../../shared/projectsV2';
import type { ReleaseGroupProjectSort } from '../shared/types';

const fetchProjects = async (filters: ProjectsFilter, sort: ReleaseGroupProjectSort, page: number, count: number) => {
  const { locators, ...restFilters } = filters;
  return post<ProjectsFilter & { sort: ReleaseGroupProjectSort, page: number, count: number }, ProjectsResponse>('/api/v2/projects', {
    ...restFilters,
    // Avoid sending any empty array of locators to the API. This wasn't necessary
    // before because qs.stringify would ignore empty arrays (when sending as URL params).
    ...(!_.isEmpty(locators) ? { locators } : {}),
    sort,
    page,
    count,
  });
};

/**
 * useProjects is a hook that fetches and returns projects from the ProjectsV2 API.
 * It will automatically re-fetch the projects when the filters or sort change.
 */
function useProjects(filters: ProjectsFilter, sort: ReleaseGroupProjectSort, page: number, count: number) {
  const onFetchProjects = useCallback(() => fetchProjects(filters, sort, page, count), [filters, sort, page, count]);
  const projectsAsyncValue = useAsyncRequest(onFetchProjects);
  const projectsResponse: ProjectsResponse = projectsAsyncValue.value ?? {
    projects: [],
    total: 0,
  };

  return {
    isLoading: projectsAsyncValue.isLoading,
    projects: projectsResponse.projects,
    projectCount: projectsResponse.total,
    projectErrors: projectsAsyncValue.errors,
  };
}

export default useProjects;
